/**
 * Proforto-matched header behaviour.
 * Sticky: fixed + compact (like Proforto stickyHeader store).
 * Mega: instant show/hide on hover (getPanelClass hidden/block).
 * Drawer: 150ms cubic-bezier transform.
 */
(() => {
  const TAX_KEY = 'pf-price-tax';
  const DESKTOP_MQ = '(min-width: 1024px)';

  const qs = (root, sel) => root.querySelector(sel);
  const qsa = (root, sel) => [...root.querySelectorAll(sel)];

  class ProfortoHeader {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.primary = qs(root, '[data-pf-primary]');
      this.primaryWrap = qs(root, '[data-pf-primary-wrap]');
      this.navItems = qsa(root, '[data-pf-nav-item]');
      this.drawer = qs(root, '[data-pf-drawer]');
      this.backdrop = qs(root, '[data-pf-backdrop]');
      this.uspItems = qsa(root, '[data-pf-usp]');
      this.taxToggle = qs(root, '[data-pf-tax]');
      this.openItem = null;
      this.closeTimer = null;
      this.uspIndex = 0;
      this.uspTimer = null;
      this.sticky = false;
      this.drawerOpen = false;
      this.drawerStack = ['root'];
      this.mq = window.matchMedia(DESKTOP_MQ);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onDocKey = this.onDocKey.bind(this);

      this.initTax();
      this.bindPrimary();
      this.bindMega();
      this.bindDrawer();
      this.bindUsps();
      this.bindSticky();
      this.onScroll();
      this.setHeaderHeight();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('keydown', this.onDocKey);
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('keydown', this.onDocKey);
      if (this.stickyObserver) this.stickyObserver.disconnect();
      this.stopUsps();
      document.body.classList.remove('pf-header-drawer-open');
    }

    bindSticky() {
      if (this.root.dataset.sticky === 'false' || !this.primaryWrap) return;
      // Sentinel at top of header shell — when it leaves the viewport, sticky engages
      let sentinel = qs(this.root, '[data-pf-sticky-sentinel]');
      if (!sentinel) {
        sentinel = document.createElement('div');
        sentinel.setAttribute('data-pf-sticky-sentinel', '');
        sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;opacity:0;';
        this.root.style.position = this.root.style.position || 'relative';
        this.root.prepend(sentinel);
      }
      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          const shouldStick = entry.boundingClientRect.top < 0 || !entry.isIntersecting;
          this.setSticky(shouldStick);
        },
        { threshold: [0, 1], rootMargin: '0px' },
      );
      this.stickyObserver.observe(sentinel);
    }

    setSticky(shouldStick) {
      if (shouldStick === this.sticky) {
        this.setHeaderHeight();
        return;
      }
      this.sticky = shouldStick;
      this.root.classList.toggle('is-sticky', shouldStick);
      if (shouldStick) this.closeAllMega();
      this.setHeaderHeight();
    }

    initTax() {
      if (!this.taxToggle) return;
      let mode = 'excl';
      try {
        mode = localStorage.getItem(TAX_KEY) || 'excl';
      } catch {
        /* ignore */
      }
      if (mode !== 'incl' && mode !== 'excl') mode = 'excl';
      this.applyTax(mode);
      this.taxToggle.addEventListener('click', () => {
        const next = this.taxToggle.getAttribute('data-tax') === 'incl' ? 'excl' : 'incl';
        this.applyTax(next);
      });
    }

    applyTax(mode) {
      if (!this.taxToggle) return;
      this.taxToggle.setAttribute('data-tax', mode);
      const label = qs(this.taxToggle, '[data-pf-tax-label]');
      if (label) label.textContent = mode === 'incl' ? 'Incl. BTW' : 'Excl. BTW';
      document.body.setAttribute('data-price-tax', mode);
      try {
        localStorage.setItem(TAX_KEY, mode);
      } catch {
        /* ignore */
      }
    }

    bindPrimary() {
      const openBtn = qs(this.root, '[data-pf-open-drawer]');
      if (openBtn) {
        openBtn.addEventListener('click', () => {
          openBtn.setAttribute('aria-expanded', 'true');
          this.openDrawer();
        });
      }
    }

    bindMega() {
      for (const item of this.navItems) {
        const panel = qs(item, '[data-pf-mega]');
        if (!panel) continue;
        const caret = qs(item, '[data-pf-mega-caret]');

        // Proforto: mouseenter opens immediately (no delay)
        item.addEventListener('mouseenter', () => {
          if (!this.mq.matches) return;
          clearTimeout(this.closeTimer);
          this.openMega(item);
        });
        item.addEventListener('mouseleave', () => {
          if (!this.mq.matches) return;
          // tiny delay so diagonal mouse path still works
          this.closeTimer = setTimeout(() => this.closeMega(item), 80);
        });

        if (caret) {
          caret.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (item.classList.contains('is-open')) this.closeMega(item);
            else this.openMega(item);
          });
        }
      }
    }

    openMega(item) {
      if (this.openItem && this.openItem !== item) this.closeMega(this.openItem);
      item.classList.add('is-open');
      this.openItem = item;
      const caret = qs(item, '[data-pf-mega-caret]');
      if (caret) caret.setAttribute('aria-expanded', 'true');
    }

    closeMega(item) {
      if (!item) return;
      item.classList.remove('is-open');
      if (this.openItem === item) this.openItem = null;
      const caret = qs(item, '[data-pf-mega-caret]');
      if (caret) caret.setAttribute('aria-expanded', 'false');
    }

    closeAllMega() {
      for (const item of this.navItems) this.closeMega(item);
    }

    bindDrawer() {
      if (!this.drawer) return;
      for (const btn of qsa(this.root, '[data-pf-close-drawer]')) {
        btn.addEventListener('click', () => this.closeDrawer());
      }
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.closeDrawer());

      for (const btn of qsa(this.drawer, '[data-pf-drill]')) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const id = btn.getAttribute('data-pf-drill');
          if (id) this.drillTo(id);
        });
      }
      for (const btn of qsa(this.drawer, '[data-pf-drill-back]')) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.drillBack();
        });
      }
    }

    openDrawer() {
      this.drawerOpen = true;
      this.root.classList.add('is-drawer-open');
      document.body.classList.add('pf-header-drawer-open');
      this.resetDrawerLevels();
      const closeBtn = qs(this.drawer, '[data-pf-close-drawer]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    closeDrawer() {
      this.drawerOpen = false;
      this.root.classList.remove('is-drawer-open');
      document.body.classList.remove('pf-header-drawer-open');
      this.resetDrawerLevels();
      const openBtn = qs(this.root, '[data-pf-open-drawer]');
      if (openBtn) {
        openBtn.setAttribute('aria-expanded', 'false');
        openBtn.focus({ preventScroll: true });
      }
    }

    resetDrawerLevels() {
      this.drawerStack = ['root'];
      for (const level of qsa(this.drawer, '[data-pf-level]')) {
        const id = level.getAttribute('data-pf-level');
        level.classList.toggle('is-active', id === 'root');
        level.classList.remove('is-left');
      }
    }

    drillTo(id) {
      const currentId = this.drawerStack[this.drawerStack.length - 1];
      const current = qs(this.drawer, `[data-pf-level="${CSS.escape(currentId)}"]`);
      const next = qs(this.drawer, `[data-pf-level="${CSS.escape(id)}"]`);
      if (!next) return;
      if (current) {
        current.classList.remove('is-active');
        current.classList.add('is-left');
      }
      next.classList.add('is-active');
      next.classList.remove('is-left');
      this.drawerStack.push(id);
    }

    drillBack() {
      if (this.drawerStack.length <= 1) return;
      const currentId = this.drawerStack.pop();
      const prevId = this.drawerStack[this.drawerStack.length - 1];
      const current = qs(this.drawer, `[data-pf-level="${CSS.escape(currentId)}"]`);
      const prev = qs(this.drawer, `[data-pf-level="${CSS.escape(prevId)}"]`);
      if (current) {
        current.classList.remove('is-active');
        current.classList.remove('is-left');
      }
      if (prev) {
        prev.classList.add('is-active');
        prev.classList.remove('is-left');
      }
    }

    bindUsps() {
      if (this.uspItems.length <= 1) return;
      this.uspItems.forEach((el, i) => el.classList.toggle('is-active', i === 0));
      if (this.reducedMotion) return;
      // Proforto rotates USPs on mobile (~transition-all duration-500)
      this.uspTimer = window.setInterval(() => {
        if (this.mq.matches) return;
        this.uspIndex = (this.uspIndex + 1) % this.uspItems.length;
        this.uspItems.forEach((el, i) => el.classList.toggle('is-active', i === this.uspIndex));
      }, 4000);
    }

    stopUsps() {
      if (this.uspTimer) window.clearInterval(this.uspTimer);
    }

    onScroll() {
      if (this.root.dataset.sticky === 'false' || !this.primaryWrap) {
        this.setHeaderHeight();
        return;
      }
      // Fallback when IO not enough (short pages / iOS): sticky after scrolling past wrap top
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const wrapTop = this.primaryWrap.getBoundingClientRect().top + y;
      const shouldStick = y > wrapTop + 4;
      this.setSticky(shouldStick);
    }

    onResize() {
      if (!this.mq.matches && this.openItem) this.closeAllMega();
      if (this.mq.matches && this.drawerOpen) this.closeDrawer();
      this.setHeaderHeight();
    }

    onDocKey(e) {
      if (e.key !== 'Escape') return;
      if (this.drawerOpen) {
        this.closeDrawer();
        return;
      }
      if (this.openItem) this.closeAllMega();
    }

    setHeaderHeight() {
      let h = 0;
      if (this.sticky && this.primary) {
        h = this.primary.offsetHeight;
      } else if (this.root) {
        h = this.root.offsetHeight;
      }
      document.body.style.setProperty('--header-group-height', `${Math.round(h)}px`);
      document.documentElement.style.setProperty('--header-height', `${Math.round(h)}px`);
    }
  }

  function boot() {
    for (const el of document.querySelectorAll('[data-pf-header]')) {
      if (el.__pfHeader) {
        el.__pfHeader.destroy();
        delete el.__pfHeader;
      }
      el.__pfHeader = new ProfortoHeader(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target?.querySelector?.('[data-pf-header]') || e.target;
    if (el?.__pfHeader) {
      el.__pfHeader.destroy();
      delete el.__pfHeader;
    }
  });
})();
